import React, { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Copy, RotateCw, ExternalLink, Check, ShieldAlert } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// The public partnership demo path. `foodzlizts.com` is the live domain we
// build outreach URLs against; preview environment shares the same path.
const DEMO_PATH = "/weekly-ads/partnership-demo";

const AdminFlippPartnershipTab = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [demoToken, setDemoToken] = useState("");
  const [rotatedAt, setRotatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const publicDomain = "https://foodzlizts.com";
  const previewOrigin = typeof window !== "undefined" ? window.location.origin : "";

  const buildUrl = (origin) =>
    demoToken ? `${origin}${DEMO_PATH}?token=${encodeURIComponent(demoToken)}` : "";

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/weekly-ads/admin/demo-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setDemoToken(data.token || "");
    } catch {
      setDemoToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rotate = async () => {
    if (!window.confirm(
      "Rotate the demo token now?\n\nAll previously-shared URLs (including any you already emailed) will stop working immediately."
    )) return;
    setRotating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/weekly-ads/admin/demo-token/rotate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setDemoToken(data.token || "");
      setRotatedAt(data.rotated_at || null);
      toast({ title: "Token rotated", description: "Fresh demo URL is ready to share." });
    } catch {
      toast({ title: "Rotate failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setRotating(false);
    }
  };

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1800);
      toast({ title: "Copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const productionUrl = buildUrl(publicDomain);
  const previewUrl = buildUrl(previewOrigin);

  return (
    <div className="space-y-4" data-testid="flipp-partnership-tab">
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-900">Flipp Partnership Demo</h2>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
              Preview mode active
            </Badge>
          </div>
          <p className="text-sm text-gray-600">
            The Weekly Ads / Grocery List Saver / Price Compare tabs are hidden from
            public users while awaiting the Flipp Media Network partnership. Use the
            demo URL below in outreach — it grants Flipp reviewers a temporary
            unblurred preview of the full experience.
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-gray-500">
            Loading demo token…
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Current token */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                  Current demo token
                </div>
                {rotatedAt && (
                  <span className="text-[10px] text-gray-400">
                    Rotated {new Date(rotatedAt).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="flipp-demo-token"
                  className="flex-1 text-xs bg-gray-100 rounded px-3 py-2 font-mono break-all"
                >
                  {demoToken || "(none)"}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(demoToken, "token")}
                  disabled={!demoToken}
                  data-testid="copy-flipp-token"
                >
                  {copiedField === "token" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Public URL — for outreach */}
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                    Production URL (share this in the pitch email)
                  </div>
                  <div className="text-[11px] text-gray-500">foodzlizts.com</div>
                </div>
                <a href={productionUrl} target="_blank" rel="noreferrer" className="text-emerald-700 hover:text-emerald-900">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="flipp-production-url"
                  className="flex-1 text-xs bg-white border border-emerald-200 rounded px-3 py-2 font-mono break-all"
                >
                  {productionUrl}
                </code>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  size="sm"
                  onClick={() => copy(productionUrl, "prodUrl")}
                  disabled={!demoToken}
                  data-testid="copy-flipp-production-url"
                >
                  {copiedField === "prodUrl" ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  Copy
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview URL — for internal QA */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                    Preview URL (internal QA)
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Same demo experience, current environment
                  </div>
                </div>
                <a href={previewUrl} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-gray-900">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="flipp-preview-url"
                  className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono break-all"
                >
                  {previewUrl}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(previewUrl, "previewUrl")}
                  disabled={!demoToken}
                  data-testid="copy-flipp-preview-url"
                >
                  {copiedField === "previewUrl" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Rotate action */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">Rotate demo token</div>
                <div className="text-xs text-gray-500">
                  Generates a fresh token. Any previously-shared URL immediately stops
                  working. Best practice: rotate after each outreach batch.
                </div>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={rotate}
                disabled={rotating}
                data-testid="rotate-flipp-token"
              >
                <RotateCw className={`h-4 w-4 mr-1 ${rotating ? "animate-spin" : ""}`} />
                {rotating ? "Rotating…" : "Rotate token now"}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminFlippPartnershipTab;
